import type { IChannel } from "containers/Channels/types";
import type { ChannelParams } from "containers/TermFormContainer";
import type { TFunction } from "i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";
import { ChannelType } from "store/types";

import { getCalculationBaseColumnOptions, resolveCalculationBaseColumn } from "./calculationBaseColumnUtils";

/**
 * The milestone accumulation column is contract-scoped. It defaults to the calculation-base column
 * (ratio 1 -> classic behavior) and is constrained by that choice:
 * - quantity base -> milestones are locked to quantity
 * - CA base (turnover / turnover_1 / turnover_2) -> milestones may use quantity or that same CA column
 */
export const resolveMilestoneBaseColumn = (
    storedValue: CalculationBaseColumn | null | undefined,
    baseColumn: CalculationBaseColumn,
): CalculationBaseColumn => {
    if (baseColumn === "quantity") return "quantity";

    const resolved = storedValue ?? baseColumn;
    if (resolved === "quantity" || resolved === baseColumn) return resolved;

    // Stored value no longer valid for the selected base (e.g. base switched from turnover_1 to turnover).
    return baseColumn;
};

export const isMilestoneBaseColumnLocked = (baseColumn: CalculationBaseColumn): boolean => baseColumn === "quantity";

/**
 * Milestone column of contracts saved before the bases moved to the contract: their thresholds were
 * stored in cents only on plain CA channels. Used when nothing carries the contract columns
 * (old templates, CSV rows of a mass update).
 */
export const getLegacyMilestoneBaseColumn = (
    channel: Partial<Pick<IChannel, "type" | "usesAlternativeMilestoneAmount">> | undefined,
): CalculationBaseColumn =>
    channel?.type === ChannelType.AMOUNT_BASED && !channel.usesAlternativeMilestoneAmount ? "turnover" : "quantity";

/** Milestone thresholds in CA use the currency symbol; quantity uses the "ventes" label. */
export const isMilestoneThresholdAmountBased = (milestoneBaseColumn: CalculationBaseColumn): boolean =>
    milestoneBaseColumn !== "quantity";

/** Resolved milestone column for a contract channel (defaults from calculation base). */
export const resolveChannelMilestoneBaseColumn = (
    channel: Pick<IChannel, "type" | "calculationMethod">,
    existingChannel: ChannelParams | undefined,
): CalculationBaseColumn => {
    const baseColumn = resolveCalculationBaseColumn(channel, existingChannel?.calculationBaseColumn);
    return resolveMilestoneBaseColumn(existingChannel?.milestoneBaseColumn, baseColumn);
};

/**
 * Milestone-column options derived from the selected calculation base (not the channel type).
 */
export const getMilestoneBaseColumnOptions = (
    baseColumn: CalculationBaseColumn,
    labels: ICalculationBaseColumnLabel[],
    t: TFunction,
): { value: CalculationBaseColumn; label: string }[] => {
    const labelByValue = Object.fromEntries(
        getCalculationBaseColumnOptions(labels, t).map((option) => [option.value, option.label]),
    ) as Partial<Record<CalculationBaseColumn, string>>;

    const allowedColumns: CalculationBaseColumn[] = baseColumn === "quantity" ? ["quantity"] : ["quantity", baseColumn];

    return allowedColumns.flatMap((value) =>
        labelByValue[value] != null ? [{ value, label: labelByValue[value] as string }] : [],
    );
};
